// Pure company schema shared by web enrichment and the extension worker.
const text = (...values) => values.find((value) => typeof value === "string" && value.trim())?.trim() || "";
export const positiveCompanyInteger = (...values) => {
  for (const value of values) {
    if (value == null || value === "" || typeof value === "boolean") continue;
    const number = typeof value === "number" ? value : Number(String(value).replace(/,/g, "").trim());
    if (Number.isSafeInteger(number) && number > 0) return number;
  }
  return null;
};

export function normalizeCompanyYear(value) {
  const year = positiveCompanyInteger(value?.year ?? value);
  return year >= 1000 && year <= 9999 ? year : null;
}

export function normalizeEmployeeCountRange(value) {
  if (!value || typeof value !== "object") return null;
  const start = positiveCompanyInteger(value.start ?? value.min);
  const rawEnd = Object.hasOwn(value, "end") ? value.end : value.max;
  if (!start || rawEnd === undefined) return null;
  if (rawEnd === null) return { start, end: null };
  const end = positiveCompanyInteger(rawEnd);
  if (!end) return null;
  // RC uses end < start (e.g. 10001, 1) for an open-ended upper bracket.
  return { start, end: end < start ? null : end };
}

export function normalizeCompanySpecialities(value) {
  const values = Array.isArray(value) ? value : typeof value === "string" ? value.split(/[,;\n]/) : [];
  return [...new Set(values.filter((item) => typeof item === "string").map((item) => item.trim()).filter(Boolean))];
}

export function normalizeCompanyData(company = {}) {
  company ??= {};
  const rawId = String(company.id || "");
  return {
    id: text(String(company.linkedInId || company.linkedinId || company.company_ln_id || ""),
      rawId.startsWith("com_") ? "" : rawId),
    rc_id: text(company.rc_id, company.rcId, company.company_rc_id, rawId.startsWith("com_") ? rawId : ""),
    name: text(company.companyName, company.name, company.company_name),
    industry: text(company.industry, company.company_industry),
    ln_url: text(company.linkedInUrl, company.linkedinUrl, company.ln_url, company.company_ln_url),
    url: text(company.websiteUrl, company.url, company.company_url),
    logo: text(company.logoUrl, company.logo, company.company_logo),
    phone: text(company.phone, company.company_phone),
    tagline: text(company.tagline, company.company_tagline),
    description: text(company.description, company.company_description),
    employee_count: positiveCompanyInteger(company.employeesCount, company.employeeCount, company.employee_count, company.company_employee_count),
    follower_count: positiveCompanyInteger(company.followersCount, company.followerCount, company.follower_count, company.company_follower),
    employee_count_range: normalizeEmployeeCountRange(company.employeeCountRange ?? company.employee_count_range ?? company.company_employee_count_range),
    specialities: normalizeCompanySpecialities(company.specialities ?? company.specialties ?? company.company_specialities),
    founded_on: normalizeCompanyYear(company.foundedOn ?? company.founded_on ?? company.company_founded_on),
  };
}

export function buildCompanyFields(company = {}, previous = {}, { omitMissingFacts = false } = {}) {
  const next = normalizeCompanyData(company);
  const old = normalizeCompanyData(previous);
  const identity = (value) => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  const sameCompany = next.id && old.id ? next.id === old.id
    : next.rc_id && old.rc_id ? next.rc_id === old.rc_id
    : next.ln_url && old.ln_url ? identity(next.ln_url) === identity(old.ln_url)
    : Boolean(next.name && old.name && identity(next.name) === identity(old.name));
  if (sameCompany) {
    for (const field of Object.keys(next)) {
      if (next[field] == null || next[field] === "" || (Array.isArray(next[field]) && !next[field].length)) next[field] = old[field];
    }
  }
  // Merge saves must not erase previously stored facts when RC omits them.
  if (omitMissingFacts) {
    for (const field of ["employee_count", "follower_count", "employee_count_range", "description", "specialities"]) {
      if (next[field] == null || next[field] === "" || (Array.isArray(next[field]) && !next[field].length)) delete next[field];
    }
  }
  return next;
}

export function mergeCompanyData(company = {}, previous = {}) {
  const next = normalizeCompanyData(company);
  const old = normalizeCompanyData(previous);
  return Object.fromEntries(
    Object.entries(next).map(([field, value]) => {
      const isMissing =
        value == null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      return [field, isMissing ? old[field] ?? value : value];
    }),
  );
}
